/**
 * vps-content.ts — innhold for /vps (norsk + engelsk)
 *
 * ⚠ PLASSHOLDERE: størrelser (vCPU, RAM, lagring, trafikk), priser, operativsystemer,
 * funksjoner som snapshots og brannmur, FAQ og kontaktadresse er foreløpige og MÅ
 * erstattes med det som faktisk leveres før siden publiseres. Teknisk spesifikasjon er
 * det kunder sammenligner på, så alle tall og funksjoner må leses gjennom av eier.
 * All redigering gjøres her — komponentene har ingen egne tekster eller tall.
 *
 * Offentlig side: statisk innhold i frontend er tillatt. Når prisene skal styres fra
 * systemet, erstattes denne filen av et API-kall (VpsContent i src/types/vps.ts er
 * kontrakten).
 *
 * BEKREFT før publisering: støttede operativsystemer, lagringstype (SSD/NVMe),
 * snapshots, brannmur, skalering uten ombygging, trafikkgrenser, support-nivåer.
 */

import type { Locale } from '@/types/landing';
import type { VpsContent } from '@/types/vps';

/** TODO: bekreft riktig kontaktadresse (admin@ntune.no er den eneste i koden i dag). */
const CONTACT = 'mailto:admin@ntune.no?subject=inVPS';

const no: VpsContent = {
  seoTitle: 'inVPS — virtuelle servere (VPS) fra inSuite',
  ui: {
    skipToContent: 'Hopp til innhold',
    navLabel: 'Hovednavigasjon',
    languageLabel: 'Språk',
    themeLabel: 'Tema',
    themeToDark: 'Bytt til mørkt tema',
    themeToLight: 'Bytt til lyst tema',
    login: 'Logg inn',
  },
  breadcrumb: {
    label: 'Brødsmulesti',
    links: [{ label: 'Hjem', href: '/' }],
    current: 'inVPS',
  },
  sectionNav: {
    label: 'Seksjoner',
    items: [
      { id: 'oversikt', label: 'Oversikt' },
      { id: 'funksjoner', label: 'Funksjoner' },
      { id: 'pakker', label: 'Pakker' },
      { id: 'sammenlign', label: 'Sammenlign' },
      { id: 'bruk', label: 'Bruksområder' },
      { id: 'sporsmal', label: 'Spørsmål' },
    ],
    cta: { label: 'Se pakkene', href: '#pakker' },
  },
  hero: {
    heading: 'inVPS',
    subheading:
      'Virtuelle servere (VPS) med full rotilgang, eget operativsystem og rask lagring, i fire størrelser du kan bytte mellom når behovet vokser.',
    primaryCta: { label: 'Se pakker og priser', href: '#pakker' },
    secondaryCta: { label: 'Dette får du', href: '#funksjoner' },
    illustrationLabel: 'Illustrasjon av server-rack, terminal og virtuelle servere i tre størrelser',
  },
  overview: {
    eyebrow: 'Oversikt',
    heading: 'Din egen server. Din kontroll.',
    body: [
      'inVPS gir deg en virtuell server med eget operativsystem og full rotilgang. Du installerer det du trenger og konfigurerer serveren slik du vil.',
      'Du styrer selv, og velger en størrelse som passer. Trenger du ikke administrere en server selv, passer inHost bedre: webhotell der vi tar driften.',
    ],
  },
  features: {
    eyebrow: 'Funksjoner',
    heading: 'Dette får du',
    intro: 'Det du trenger for å kjøre dine egne tjenester.',
    panelLabel: 'Illustrasjon av server-rack og virtuelle servere i økende størrelse',
    items: [
      { title: 'Full rotilgang', body: 'Logg inn med SSH, og installer og konfigurer det du trenger.' },
      { title: 'Velg operativsystem', body: 'Start fra en av de støttede Linux-distribusjonene.' },
      { title: 'Rask lagring', body: 'Serveren kjører på SSD-lagring, slik at tjenestene dine svarer raskt.' },
      { title: 'Snapshots og backup', body: 'Ta øyeblikksbilder av serveren før endringer, og gjenopprett ved behov.' },
      { title: 'Nettverk og brannmur', body: 'Egen IP-adresse og en brannmur du styrer selv.' },
      { title: 'Skaler når behovet vokser', body: 'Gå opp en størrelse uten å bygge serveren på nytt.' },
    ],
  },
  promo: {
    title: 'Usikker på om du trenger en VPS?',
    body: 'Trenger du ikke full kontroll, er inHost enklere: webhotell der vi tar driften.',
    cta: { label: 'Se inHost', href: '/webhotell' },
  },
  plans: {
    eyebrow: 'Pakker',
    heading: 'Pakker og priser',
    intro: 'Velg størrelsen som passer, og gå opp en størrelse når behovet vokser.',
    fromLabel: 'Fra',
    footnote: '*Prisene er veiledende, per måned og eksklusiv mva.',
    items: [
      {
        id: 's',
        name: 'VPS S',
        summary: 'For små tjenester og testing.',
        price: '99',
        priceNote: 'kr / mnd',
        features: ['1 vCPU', '2 GB RAM', '40 GB SSD', '1 TB trafikk', 'Full rotilgang'],
        cta: { label: 'Bestill VPS S', href: CONTACT },
      },
      {
        id: 'm',
        name: 'VPS M',
        summary: 'For nettsteder og API-er.',
        price: '199',
        priceNote: 'kr / mnd',
        inheritsLabel: 'Alt i VPS S, pluss',
        features: ['2 vCPU', '4 GB RAM', '80 GB SSD', '2 TB trafikk', 'Snapshots'],
        cta: { label: 'Bestill VPS M', href: CONTACT },
        highlighted: true,
        highlightLabel: 'Anbefalt',
      },
      {
        id: 'l',
        name: 'VPS L',
        summary: 'For databaser og travle tjenester.',
        price: '399',
        priceNote: 'kr / mnd',
        inheritsLabel: 'Alt i VPS M, pluss',
        features: ['4 vCPU', '8 GB RAM', '160 GB SSD', '4 TB trafikk', 'Prioritert support'],
        cta: { label: 'Bestill VPS L', href: CONTACT },
      },
      {
        id: 'xl',
        name: 'VPS XL',
        summary: 'For krevende arbeidslaster.',
        price: '799',
        priceNote: 'kr / mnd',
        inheritsLabel: 'Alt i VPS L, pluss',
        features: ['8 vCPU', '16 GB RAM', '320 GB SSD', '8 TB trafikk', 'Prioritert support'],
        cta: { label: 'Bestill VPS XL', href: CONTACT },
      },
    ],
  },
  comparison: {
    eyebrow: 'Sammenlign',
    heading: 'Sammenlign pakkene',
    intro: 'Se hva hver størrelse inneholder.',
    featureLabel: 'Funksjon',
    yes: 'Inkludert',
    no: 'Ikke inkludert',
    rows: [
      { label: 'Maskinvare', values: [], group: true },
      { label: 'vCPU', values: ['1', '2', '4', '8'] },
      { label: 'RAM', values: ['2 GB', '4 GB', '8 GB', '16 GB'] },
      { label: 'SSD-lagring', values: ['40 GB', '80 GB', '160 GB', '320 GB'] },
      { label: 'Trafikk per måned', values: ['1 TB', '2 TB', '4 TB', '8 TB'] },
      { label: 'Funksjoner og support', values: [], group: true },
      { label: 'Full rotilgang', values: [true, true, true, true] },
      { label: 'Brannmur', values: [true, true, true, true] },
      { label: 'Snapshots', values: [false, true, true, true] },
      { label: 'Prioritert support', values: [false, false, true, true] },
    ],
  },
  useCases: {
    eyebrow: 'Bruksområder',
    heading: 'Slik brukes det',
    items: [
      {
        icon: 'api',
        title: 'Webapplikasjoner og API-er',
        body: 'Kjør dine egne applikasjoner og API-er med den stacken du velger selv.',
        link: { label: 'Be om tilbud', href: CONTACT },
      },
      {
        icon: 'code',
        title: 'Utvikling og test',
        body: 'Sett opp utviklings- og testmiljøer som ligner produksjon, og ta snapshots før større endringer.',
        link: { label: 'Be om tilbud', href: CONTACT },
      },
      {
        icon: 'virtual',
        title: 'Egne tjenester',
        body: 'Kjør databaser, køer, VPN eller andre tjenester på en server du styrer selv.',
        link: { label: 'Kontakt oss', href: CONTACT },
      },
    ],
  },
  related: {
    eyebrow: 'Relatert',
    heading: 'Relaterte tjenester',
    items: [
      { icon: 'server', name: 'inHost — webhotell', href: '/webhotell' },
      { icon: 'wordpress', name: 'inWordPress — WordPress-hosting', href: '/wordpress' },
      { icon: 'nodejs', name: 'inNode — Node.js-hosting', href: '/nodejs' },
      { icon: 'chip', name: 'inGPU — GPU-deploy', href: '/gpu' },
      { icon: 'globe', name: 'inDomain — domener', href: '/domener/sok' },
    ],
  },
  faq: {
    eyebrow: 'Spørsmål',
    heading: 'Vanlige spørsmål',
    items: [
      {
        question: 'Hva er forskjellen på VPS og webhotell?',
        answer:
          'Med en VPS får du en egen virtuell server og styrer den selv, med rotilgang og eget operativsystem. Med webhotell (inHost) står vi for driften, og du bruker det ferdig oppsatte miljøet.',
      },
      { question: 'Kan jeg bytte størrelse senere?', answer: 'Ja, du kan gå opp til en større størrelse når behovet vokser. Ta kontakt, så hjelper vi deg.' },
      { question: 'Hvilke operativsystemer kan jeg bruke?', answer: 'Du kan starte fra en av de støttede Linux-distribusjonene. Ta kontakt for oversikt over hvilke.' },
      { question: 'Hvordan bestiller jeg?', answer: 'Send oss en e-post med størrelsen du ønsker, så tar vi kontakt for å sette opp serveren din.' },
    ],
  },
  cta: {
    heading: 'Klar til å bestille?',
    body: 'Kontakt oss, så setter vi opp serveren din.',
    primary: { label: 'Kontakt oss', href: CONTACT },
  },
  footer: {
    tagline: 'inVPS — virtuelle servere med full kontroll, fra inSuite.',
    legal: 'inSuite',
    platformLabel: 'inSuite',
    sectionsLabel: 'inVPS',
  },
};

const en: VpsContent = {
  seoTitle: 'inVPS — virtual private servers (VPS) from inSuite',
  ui: {
    skipToContent: 'Skip to content',
    navLabel: 'Main navigation',
    languageLabel: 'Language',
    themeLabel: 'Theme',
    themeToDark: 'Switch to dark theme',
    themeToLight: 'Switch to light theme',
    login: 'Log in',
  },
  breadcrumb: {
    label: 'Breadcrumb',
    links: [{ label: 'Home', href: '/' }],
    current: 'inVPS',
  },
  sectionNav: {
    label: 'Sections',
    items: [
      { id: 'oversikt', label: 'Overview' },
      { id: 'funksjoner', label: 'Features' },
      { id: 'pakker', label: 'Plans' },
      { id: 'sammenlign', label: 'Compare' },
      { id: 'bruk', label: 'Use cases' },
      { id: 'sporsmal', label: 'FAQ' },
    ],
    cta: { label: 'See the plans', href: '#pakker' },
  },
  hero: {
    heading: 'inVPS',
    subheading:
      'Virtual private servers (VPS) with full root access, your own operating system and fast storage, in four sizes you can move between as your needs grow.',
    primaryCta: { label: 'See plans and pricing', href: '#pakker' },
    secondaryCta: { label: 'What you get', href: '#funksjoner' },
    illustrationLabel: 'Illustration of a server rack, a terminal and virtual servers in three sizes',
  },
  overview: {
    eyebrow: 'Overview',
    heading: 'Your own server. Your control.',
    body: [
      'inVPS gives you a virtual server with your own operating system and full root access. You install what you need and configure the server the way you want.',
      'You are in control, and pick a size that fits. If you would rather not manage a server yourself, inHost is a better fit: web hosting where we run the operations.',
    ],
  },
  features: {
    eyebrow: 'Features',
    heading: 'What you get',
    intro: 'What you need to run your own services.',
    panelLabel: 'Illustration of a server rack and virtual servers of increasing size',
    items: [
      { title: 'Full root access', body: 'Log in over SSH, and install and configure what you need.' },
      { title: 'Choose your operating system', body: 'Start from one of the supported Linux distributions.' },
      { title: 'Fast storage', body: 'The server runs on SSD storage, so your services respond quickly.' },
      { title: 'Snapshots and backup', body: 'Take snapshots of the server before changes, and restore when needed.' },
      { title: 'Network and firewall', body: 'Your own IP address and a firewall you control.' },
      { title: 'Scale as your needs grow', body: 'Move up a size without rebuilding the server.' },
    ],
  },
  promo: {
    title: 'Not sure you need a VPS?',
    body: 'If you do not need full control, inHost is simpler: web hosting where we run the operations.',
    cta: { label: 'See inHost', href: '/webhotell' },
  },
  plans: {
    eyebrow: 'Plans',
    heading: 'Plans and pricing',
    intro: 'Pick the size that fits, and move up a size as your needs grow.',
    fromLabel: 'From',
    footnote: '*Prices are indicative, per month and exclude VAT.',
    items: [
      {
        id: 's',
        name: 'VPS S',
        summary: 'For small services and testing.',
        price: '99',
        priceNote: 'NOK / month',
        features: ['1 vCPU', '2 GB RAM', '40 GB SSD', '1 TB traffic', 'Full root access'],
        cta: { label: 'Order VPS S', href: CONTACT },
      },
      {
        id: 'm',
        name: 'VPS M',
        summary: 'For websites and APIs.',
        price: '199',
        priceNote: 'NOK / month',
        inheritsLabel: 'Everything in VPS S, plus',
        features: ['2 vCPU', '4 GB RAM', '80 GB SSD', '2 TB traffic', 'Snapshots'],
        cta: { label: 'Order VPS M', href: CONTACT },
        highlighted: true,
        highlightLabel: 'Recommended',
      },
      {
        id: 'l',
        name: 'VPS L',
        summary: 'For databases and busy services.',
        price: '399',
        priceNote: 'NOK / month',
        inheritsLabel: 'Everything in VPS M, plus',
        features: ['4 vCPU', '8 GB RAM', '160 GB SSD', '4 TB traffic', 'Priority support'],
        cta: { label: 'Order VPS L', href: CONTACT },
      },
      {
        id: 'xl',
        name: 'VPS XL',
        summary: 'For demanding workloads.',
        price: '799',
        priceNote: 'NOK / month',
        inheritsLabel: 'Everything in VPS L, plus',
        features: ['8 vCPU', '16 GB RAM', '320 GB SSD', '8 TB traffic', 'Priority support'],
        cta: { label: 'Order VPS XL', href: CONTACT },
      },
    ],
  },
  comparison: {
    eyebrow: 'Compare',
    heading: 'Compare the plans',
    intro: 'See what each size includes.',
    featureLabel: 'Feature',
    yes: 'Included',
    no: 'Not included',
    rows: [
      { label: 'Hardware', values: [], group: true },
      { label: 'vCPU', values: ['1', '2', '4', '8'] },
      { label: 'RAM', values: ['2 GB', '4 GB', '8 GB', '16 GB'] },
      { label: 'SSD storage', values: ['40 GB', '80 GB', '160 GB', '320 GB'] },
      { label: 'Traffic per month', values: ['1 TB', '2 TB', '4 TB', '8 TB'] },
      { label: 'Features and support', values: [], group: true },
      { label: 'Full root access', values: [true, true, true, true] },
      { label: 'Firewall', values: [true, true, true, true] },
      { label: 'Snapshots', values: [false, true, true, true] },
      { label: 'Priority support', values: [false, false, true, true] },
    ],
  },
  useCases: {
    eyebrow: 'Use cases',
    heading: 'How it is used',
    items: [
      {
        icon: 'api',
        title: 'Web applications and APIs',
        body: 'Run your own applications and APIs on the stack of your choice.',
        link: { label: 'Request a quote', href: CONTACT },
      },
      {
        icon: 'code',
        title: 'Development and testing',
        body: 'Set up development and test environments that resemble production, and take snapshots before bigger changes.',
        link: { label: 'Request a quote', href: CONTACT },
      },
      {
        icon: 'virtual',
        title: 'Your own services',
        body: 'Run databases, queues, a VPN or other services on a server you control.',
        link: { label: 'Contact us', href: CONTACT },
      },
    ],
  },
  related: {
    eyebrow: 'Related',
    heading: 'Related services',
    items: [
      { icon: 'server', name: 'inHost — web hosting', href: '/webhotell' },
      { icon: 'wordpress', name: 'inWordPress — WordPress hosting', href: '/wordpress' },
      { icon: 'nodejs', name: 'inNode — Node.js hosting', href: '/nodejs' },
      { icon: 'chip', name: 'inGPU — GPU deploy', href: '/gpu' },
      { icon: 'globe', name: 'inDomain — domains', href: '/domener/sok' },
    ],
  },
  faq: {
    eyebrow: 'FAQ',
    heading: 'Frequently asked questions',
    items: [
      {
        question: 'What is the difference between a VPS and web hosting?',
        answer:
          'With a VPS you get your own virtual server and manage it yourself, with root access and your own operating system. With web hosting (inHost) we run the operations, and you use the ready-made environment.',
      },
      { question: 'Can I change size later?', answer: 'Yes, you can move up to a larger size as your needs grow. Get in touch and we will help you.' },
      { question: 'Which operating systems can I use?', answer: 'You can start from one of the supported Linux distributions. Get in touch for the list.' },
      { question: 'How do I order?', answer: 'Send us an email with the size you want, and we will get in touch to set up your server.' },
    ],
  },
  cta: {
    heading: 'Ready to order?',
    body: 'Contact us and we will set up your server.',
    primary: { label: 'Contact us', href: CONTACT },
  },
  footer: {
    tagline: 'inVPS — virtual servers with full control, from inSuite.',
    legal: 'inSuite',
    platformLabel: 'inSuite',
    sectionsLabel: 'inVPS',
  },
};

export const VPS_CONTENT: Record<Locale, VpsContent> = { no, en };
